import { clearAlertsThunk } from 'actions/alerts/AlertsActions';
import {
    fetchOnlineBookingStaffAvailabilityThunk,
    goToNextAvailableDateThunk,
    setOnlineBookingDate,
    setOnlineBookingTime,
    submitOnlineBookingSelectDateTimeThunk
} from 'actions/onlineBooking/OnlineBookingActions';
import { PoweredByCuddles } from 'components/Cuddles/PoweredBy/PoweredByCuddles';
import { DatePickerSpinner } from 'components/Form/DatePickerSpinner/DatePickerSpinner';
import ErrorList from 'components/Form/ErrorList/ErrorList';
import { SelectableTag } from 'components/Form/SelectableTag/SelectableTag';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { OnlineBookingResume } from 'components/OnlineBooking/Resume/OnlineBookingResume';
import { Button } from 'components/UI/Button/Button';
import { Spinner } from 'components/UI/Spinner/Spinner';
import { useMarketplace } from 'hooks/useMarketplace';
import { useMediaQuery } from 'hooks/useMediaQuery';
import moment, { Moment } from 'moment-timezone';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getOlbDateLimits } from 'reducers/masterData/MasterDataSelectors';
import {
    getOnlineBookingAvailability,
    getOnlineBookingDate,
    getOnlineBookingLoading,
    getOnlineBookingTime
} from 'reducers/onlineBooking/OnlineBookingSelectors';
import { AlertScreen } from 'types/Alert';
import { AvailabilityHour } from 'types/Availability';

import classes from './OnlineBookingSelectDateTimeStep.module.scss';

export const DateTimeSelector: React.FC = () => {
    const dispatch = useDispatch();
    const { timeZone, onlineBookingPreferences } = useMarketplace();
    const isMobile = useMediaQuery('mobile-and-tablet');

    const olbDateLimits = useSelector(getOlbDateLimits);
    const isLoading = useSelector(getOnlineBookingLoading);
    const selectedDate = useSelector(getOnlineBookingDate);
    const bookingTime = useSelector(getOnlineBookingTime);
    const { availableHours, morningHours, afternoonHours } = useSelector(
        getOnlineBookingAvailability
    );

    const today = moment().tz(timeZone);

    const isFirstRender = React.useRef(true);

    const [isNextAvailableDateFetched, setIsNextAvailableDateFetched] = React.useState(false);

    const minDate = React.useMemo(
        () =>
            today
                .clone()
                .add(
                    olbDateLimits.from.find(
                        limit => limit.id === onlineBookingPreferences.bookingDateLimits.from
                    )?.offsetSeconds ?? 0,
                    'seconds'
                ),
        [today, olbDateLimits, onlineBookingPreferences.bookingDateLimits.from]
    );

    const maxDate = React.useMemo(
        () =>
            today
                .clone()
                .add(
                    olbDateLimits.to.find(
                        limit => limit.id === onlineBookingPreferences.bookingDateLimits.to
                    )?.offsetSeconds ?? 0,
                    'seconds'
                ),
        [today, olbDateLimits, onlineBookingPreferences.bookingDateLimits.to]
    );

    const dateChangeHandler = (date: Moment) => {
        if (!date.isSame(selectedDate, 'day')) {
            dispatch(clearAlertsThunk());
        }

        dispatch(fetchOnlineBookingStaffAvailabilityThunk(date.clone()));
        dispatch(setOnlineBookingDate(date.clone()));
    };

    const timeChangeHandler = ({ time, staffAvailable }: AvailabilityHour) => {
        const [hour, minute] = time.split(':');
        const isPM = time.includes('pm');
        const hourNumber = parseInt(hour);

        let hour24 = hourNumber;
        if (isPM && hourNumber !== 12) {
            hour24 = hourNumber + 12;
        } else if (!isPM && hourNumber === 12) {
            hour24 = 0;
        }

        dispatch(
            setOnlineBookingTime({
                staffId: Number(staffAvailable[0]),
                hour: hour24,
                minute: parseInt(minute),
                label: time
            })
        );
    };

    React.useEffect(() => {
        if (isLoading) return;

        let defaultDate = selectedDate || today.clone();

        if (defaultDate.isBefore(minDate)) {
            defaultDate = minDate;
        }

        dateChangeHandler(defaultDate);
        // eslint-disable-next-line
    }, []);

    React.useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (availableHours.length > 0 && !isNextAvailableDateFetched) {
            setIsNextAvailableDateFetched(true);
        }

        if (
            !isLoading &&
            selectedDate &&
            selectedDate.isSame(minDate, 'day') &&
            availableHours.length === 0 &&
            !isNextAvailableDateFetched
        ) {
            dispatch(goToNextAvailableDateThunk(selectedDate));
        }
        // eslint-disable-next-line
    }, [availableHours, isNextAvailableDateFetched]);

    return (
        <>
            {!isLoading && (
                <ErrorList screen={AlertScreen.OnlineBookingSelectDateTime} className="mb-6" />
            )}

            {selectedDate && (
                <DatePickerSpinner
                    mode={isMobile ? 'month' : 'week'}
                    minDate={minDate}
                    maxDate={maxDate}
                    timeZone={timeZone}
                    selectedDate={selectedDate}
                    onChange={dateChangeHandler}
                />
            )}

            {!isLoading && availableHours.length === 0 && (
                <div className={classes.timeGroup}>
                    <p className={classes.noAvailability}>Not available</p>
                </div>
            )}

            {!isLoading && availableHours.length > 0 && (
                <>
                    <div className={classes.timeGroup}>
                        <p className={classes.timeGroupLabel}>Morning</p>

                        {morningHours.length === 0 && (
                            <p className={classes.noAvailability}>Not available</p>
                        )}

                        <div className={classes.timeGrid}>
                            {morningHours.map(hour => (
                                <SelectableTag
                                    key={hour.time}
                                    className={classes.timeTag}
                                    selected={bookingTime?.label === hour.time}
                                    onClick={() => timeChangeHandler(hour)}
                                >
                                    {hour.time}
                                </SelectableTag>
                            ))}
                        </div>
                    </div>

                    <div className={classes.timeGroup}>
                        <p className={classes.timeGroupLabel}>Afternoon</p>

                        {afternoonHours.length === 0 && (
                            <p className={classes.noAvailability}>Not available</p>
                        )}

                        <div className={classes.timeGrid}>
                            {afternoonHours.map(hour => (
                                <SelectableTag
                                    key={hour.time}
                                    className={classes.timeTag}
                                    selected={bookingTime?.label === hour.time}
                                    onClick={() => timeChangeHandler(hour)}
                                >
                                    {hour.time}
                                </SelectableTag>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export const OnlineBookingSelectDateTimeStep: React.FC = () => {
    const dispatch = useDispatch();
    const marketplace = useMarketplace();
    const bookingTime = useSelector(getOnlineBookingTime);

    const isLoading = useSelector(getOnlineBookingLoading);

    const submitHandler = () => {
        dispatch(submitOnlineBookingSelectDateTimeThunk());
    };

    return (
        <>
            <LeftColumn contentClassName="flex flex-col flex-1">
                <h1 className="h1 underlined w-full mb-5">Select date & time</h1>

                <DateTimeSelector />

                {isLoading && (
                    <div className="w-full text-center mt-6">
                        <Spinner size={50} />
                    </div>
                )}

                <div className="mt-auto hide-on-mobile-and-tablet pt-6">
                    <PoweredByCuddles />
                </div>
            </LeftColumn>

            <RightColumn
                poweredByCuddles
                footer={
                    <Button fullWidth disabled={!bookingTime} onClick={submitHandler}>
                        Continue
                    </Button>
                }
            >
                <div className="flex flex-col gap-5 hide-on-mobile-and-tablet">
                    <MarketplaceInfo inverted marketplace={marketplace} />

                    <OnlineBookingResume />
                </div>
            </RightColumn>
        </>
    );
};
